import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DropdownFilterOptions } from 'primeng/dropdown';
import {
  Component,
  EventEmitter,
  Input,
  model,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  InvoiceDtoWithValidity,
  MapAdditionalIdentifier,
  MappedBillingDocument,
} from '../../../../accounts/types';
import { CustomToastService } from '../../../services/custom-toast.service';
import {
  additionalIdentifierNoTaxOptions,
  additionalIdentifierOptions,
  cityListsByCountry,
  countriesList,
  countryCode,
} from '../../../../accounts/constants';
import { RegexEnum } from '../../../model/enums/RegexPattern';
import { AccountService } from '../../../../accounts/account.service';
import { InvoiceZatcaType, ReSubmitToZatcaInput } from '../../../types';
import { getPhoneNumberValidator } from '../../../utils';
import { handlePostalCodeValidationBasedOnCountry } from '../../../../accounts/components/checkout-v2/validators/checkoutform.validator';

@Component({
  selector: 'rwa-billing-info-dialog',
  templateUrl: './billing-info-dialog.component.html',
  styleUrl: './billing-info-dialog.component.scss',
})
export class BillingInfoDialogComponent implements OnInit {
  readonly translationKey = 'createInvoice.';

  readonly monshaatCommercialRegistration = 'Monshaat Commercial Registration';

  selectedIdentifier = null;

  showCustomCityNameField = false;

  showMonshaatFields = false;

  crmMonshaatOrderNumber: string | null = null;

  crmMonshaatCompanyName: string | null = null;

  crmMonshaatCommercialRegistration: string | null = null;

  @Input() billingInfoDialogInput: ReSubmitToZatcaInput;

  invoiceData = model.required<InvoiceDtoWithValidity>();

  showModal: null | string = null;

  filteredCities = [];

  selectedCountry = { name: 'المملكة العربية السعودية' };

  selectedCity = { name: '' };

  cities = cityListsByCountry['المملكة العربية السعودية'];

  countries = countriesList;

  readonly invoiceZatcaType = InvoiceZatcaType;

  customerForm: UntypedFormGroup;

  additionalIdentifiers = [...additionalIdentifierOptions];

  lang: string;

  countriesWithData: any[] = [
    {
      countryKey: 'SA',
      name: 'Saudi Arabia',
      localeName: 'Saudi Arabia',
      code: '966',
      currency: 'SAR',
      languages: 'ar',
      createdAt: null,
      deletedAt: null,
      updatedAt: null,
    },
  ];

  phoneSelectedCountry: any = {
    countryKey: 'SA',
    name: 'Saudi Arabia',
    localeName: 'Saudi Arabia',
    code: '966',
    currency: 'SAR',
    languages: 'ar',
    createdAt: null,
    deletedAt: null,
    updatedAt: null,
  };

  isPhoneNumberAvailable: boolean = false;

  filterValue = '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() closeDialog = new EventEmitter<any>();

  constructor(
    public fb: UntypedFormBuilder,
    public accountService: AccountService,
    private translate: TranslateService,
    public customToastService: CustomToastService,
  ) {
    this.customerForm = this.fb.group({
      vatNumber: [
        '',
        [
          Validators.maxLength(15),
          Validators.required,
          Validators.pattern(RegexEnum.numberOnly),
        ],
      ],
      nonTaxableEntity: [false],
      country: ['', [Validators.required]],
      city: ['', [Validators.required, this.cityValidator.bind(this)]],
      customCityName: [''],
      districtName: ['', [Validators.required, Validators.maxLength(149)]],
      streetName: ['', [Validators.required, Validators.maxLength(139)]],
      postalCode: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(5),
          Validators.pattern(RegexEnum.numberOnly),
        ],
      ],
      companyName: ['', [Validators.required, Validators.maxLength(149)]],
      phone: [''],
      additionalIdentifier: ['', Validators.required],
      additionalIdentifierNumber: [
        '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern(RegexEnum.numberOnly),
        ],
      ],
      monshaatCompanyName: [''],
      monshaatOrderNumber: [''],
      buildingNumber: ['', Validators.maxLength(10)],
      additionalNumber: ['', Validators.maxLength(10)],
    });
  }

  ngOnInit(): void {
    this.onFormValueChanges();
    this.selectedCountry = { name: 'المملكة العربية السعودية' };
    this.cities = cityListsByCountry[this.selectedCountry.name];

    const document = this.billingInfoDialogInput
      .document as MappedBillingDocument;
    const isMonshaatInvoice =
      document.additionalIdType === this.monshaatCommercialRegistration;
    this.showMonshaatFields = isMonshaatInvoice;

    if (document.crmInfo) {
      this.crmMonshaatOrderNumber =
        document.crmInfo.monshaatOrderNumber ?? null;
      this.crmMonshaatCompanyName =
        document.crmInfo.monshaatCompanyName ?? null;
      this.crmMonshaatCommercialRegistration =
        document.crmInfo.monshaatCommercialRegistration ?? null;
    }

    if (document.invoice_type === InvoiceZatcaType.B2C) {
      this.customerForm.get('vatNumber')?.clearValidators();
      this.customerForm.get('vatNumber')?.disable();
      this.customerForm.get('vatNumber')?.patchValue('');

      this.customerForm.get('additionalIdentifier')?.disable();

      this.nonTaxableEntity?.disable();
      this.nonTaxableEntity?.patchValue(true);

      this.customerForm.get('additionalIdentifierNumber')?.clearValidators();
      this.customerForm.get('additionalIdentifierNumber')?.disable();
      this.customerForm.get('additionalIdentifierNumber')?.patchValue('');
    }

    if (!document.phoneNumber) {
      this.isPhoneNumberAvailable = false;
      this.customerForm
        .get('phone')
        ?.setValidators([
          Validators.required,
          getPhoneNumberValidator(this.phoneSelectedCountry),
        ]);
      this.customerForm.get('phone').updateValueAndValidity();
      firstValueFrom(this.accountService.getCountries())
        .then((countries) => {
          this.countriesWithData = countries
            .map((country) => {
              const localeName =
                this.lang === 'ar'
                  ? this.translate.instant(`country.${country.countryKey}`)
                  : country.name;
              const countryWithLocale = { ...country, localeName };
              if (
                countryWithLocale.countryKey === 'SA' &&
                !this.phoneSelectedCountry
              ) {
                this.phoneSelectedCountry = countryWithLocale;
              }
              return countryWithLocale;
            })
            .sort((a, b) => a.localeName.localeCompare(b.localeName));
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.info(error);
          this.customToastService.error(error);
        });
    } else {
      this.isPhoneNumberAvailable = true;
    }
    const { NoLegalEntity } = additionalIdentifierNoTaxOptions;

    this.invoiceData.set({
      isValid: this.isFormValid(),
      vatNumber: document.vatNumber,
      companyName: document.billingAddress.company,
      additionalIdentifier:
        MapAdditionalIdentifier[document.additionalIdType] ?? NoLegalEntity,
      additionalIdentifierNumber: document.additionalIdValue,
      monshaatCompanyName: document.monshaatCompanyName,
      monshaatOrderNumber: document.monshaatOrderNumber,
      city: document.billingAddress.city,
      country: document.billingAddress.country,
      streetName: document.billingAddress.line1?.split(', ')[0],
      postalCode: document.billingAddress.zip,
      districtName: document.billingAddress.line2,
      additionalNumber: document.billingAddress.line1?.split(', ')[1] || '',
      buildingNumber: document.billingAddress.line3 || '',
      webhookTimestamp: +document.webhookTimestamp,
      email: document.email,
      customerId: document.customerId,
    });

    this.invoiceData.set({
      ...this.invoiceData(),
      isValid: this.isFormValid(),
    });

    this.fillDetailsAndDisableFieldsInForm(this.invoiceData());

    this.customerForm
      .get('additionalIdentifier')
      ?.valueChanges.subscribe((value) => {
        this.handleAdditionalIdentifierChange(value);
      });

    this.customerForm.get('city').valueChanges.subscribe((value) => {
      this.handleCityChange(value);
    });

    this.handlePostalCode(this.selectedCountry);

    this.handleMonshaatOrderValidation();
  }

  onFormValueChanges(): void {
    this.customerForm.valueChanges.subscribe(() => {
      this.invoiceData.set({
        isValid: this.isFormValid(),
        vatNumber: this.vatNumber?.value,
        companyName: this.companyName?.value,
        additionalIdentifier: this.additionalIdentifier?.value,
        additionalIdentifierNumber: this.additionalIdentifierNumber?.value,
        monshaatCompanyName: this.monshaatCompanyName?.value,
        monshaatOrderNumber: this.monshaatOrderNumber?.value,
        country: this.selectedCountry.name,
        countryCode: countryCode[this.customerForm.get('country')?.value.name],
        city:
          this.customerForm.get('customCityName')?.value ||
          this.city?.value?.name,
        districtName: this.districtName?.value ?? '',
        streetName: this.streetName?.value,
        postalCode: this.postalCode?.value,
        additionalNumber: this.additionalNumber?.value,
        buildingNumber: this.buildingNumber?.value,
        webhookTimestamp: +(
          this.billingInfoDialogInput.document as MappedBillingDocument
        ).webhookTimestamp,
        email: (this.billingInfoDialogInput.document as MappedBillingDocument)
          .email,
        customerId: (
          this.billingInfoDialogInput.document as MappedBillingDocument
        ).customerId,
        phoneNumber: this.phone.value
          ? `+${this.phoneSelectedCountry.code}${this.phone.value}`
          : '',
      });
    });
  }

  get vatNumber(): AbstractControl | null {
    return this.customerForm.get('vatNumber');
  }

  get nonTaxableEntity(): AbstractControl | null {
    return this.customerForm.get('nonTaxableEntity');
  }

  get city(): AbstractControl | null {
    return this.customerForm.get('city');
  }

  get customCityName(): AbstractControl | null {
    return this.customerForm.get('customCityName');
  }

  get country(): AbstractControl | null {
    return this.customerForm.get('country');
  }

  get districtName(): AbstractControl | null {
    return this.customerForm.get('districtName');
  }

  get streetName(): AbstractControl | null {
    return this.customerForm.get('streetName');
  }

  get postalCode(): AbstractControl | null {
    return this.customerForm.get('postalCode');
  }

  get companyName(): AbstractControl | null {
    return this.customerForm.get('companyName');
  }

  get additionalIdentifier(): AbstractControl | null {
    return this.customerForm.get('additionalIdentifier');
  }

  get additionalIdentifierNumber(): AbstractControl | null {
    return this.customerForm.get('additionalIdentifierNumber');
  }

  get buildingNumber(): AbstractControl | null {
    return this.customerForm.get('buildingNumber');
  }

  get additionalNumber(): AbstractControl | null {
    return this.customerForm.get('additionalNumber');
  }

  get monshaatCompanyName(): AbstractControl | null {
    return this.customerForm.get('monshaatCompanyName');
  }

  get monshaatOrderNumber(): AbstractControl | null {
    return this.customerForm.get('monshaatOrderNumber');
  }

  get phone(): AbstractControl | null {
    return this.customerForm.get('phone');
  }

  myResetFunction(options: DropdownFilterOptions): void {
    options.reset();
    this.filterValue = '';
  }

  filterCities(event: { query: string }): void {
    const filtered: { name: string }[] = [];
    const { query } = event;
    for (let i = 0; i < this.cities.length; i++) {
      const city = this.cities[i];
      if (city.name.toLowerCase().indexOf(query.toLowerCase()) !== -1) {
        filtered.push(city);
      }
    }
    this.filteredCities = filtered;
  }

  handleVATToggle(): void {
    const nonTaxable = this.customerForm.get('nonTaxableEntity')?.value;
    const vatNumber = 'vatNumber';
    const { NoLegalEntity, NationalID } = additionalIdentifierNoTaxOptions;

    if (nonTaxable) {
      this.customerForm.get(vatNumber)?.clearValidators();
      this.customerForm.get(vatNumber)?.disable();
      this.customerForm.get(vatNumber)?.patchValue('');
    } else {
      this.customerForm
        .get(vatNumber)
        ?.setValidators([
          Validators.required,
          Validators.maxLength(15),
          Validators.pattern(RegexEnum.numberOnly),
        ]);
      this.customerForm.get(vatNumber)?.enable();
      if (
        this.additionalIdentifier.value === NoLegalEntity ||
        this.additionalIdentifier.value === NationalID
      )
        this.additionalIdentifier.reset();
    }
    this.additionalIdentifiers = this.assignAdditionalIdentifiers();
    this.customerForm.get(vatNumber)?.updateValueAndValidity();
  }

  handleAdditionalIdentifierChange(value: string): void {
    const additionalIdentifierNumber = this.customerForm.get(
      'additionalIdentifierNumber',
    );
    const { NoLegalEntity } = additionalIdentifierNoTaxOptions;

    if (this.showMonshaatFields) {
      this.handleMonshaatOrderValidation();
      return;
    }

    if (value === NoLegalEntity) {
      additionalIdentifierNumber?.disable();
      additionalIdentifierNumber?.setValue('');
    } else {
      additionalIdentifierNumber?.enable();

      additionalIdentifierNumber?.setValidators([
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(10),
        Validators.pattern(RegexEnum.numberOnly),
      ]);

      additionalIdentifierNumber?.updateValueAndValidity();
    }
  }

  handleCityChange(value: { name: string } | string): void {
    if (typeof value === 'object' && value?.name === 'أخرى') {
      this.showCustomCityNameField = true;

      if (!this.customerForm.get('customCityName')) {
        this.customerForm.addControl(
          'customCityName',
          new FormControl('', [Validators.required, Validators.maxLength(49)]),
        );
      }
    } else {
      this.showCustomCityNameField = false;

      if (this.customerForm.get('customCityName')) {
        this.customerForm.removeControl('customCityName');
      }
    }
  }

  updateAvailableCities(): void {
    this.cities = cityListsByCountry[this.selectedCountry.name];
    if (this.cityValidator(this.city))
      this.customerForm.get('city')?.patchValue('');
    this.handlePostalCode(this.selectedCountry);
  }

  cityValidator(control: AbstractControl): { [key: string]: unknown } | null {
    const validCity = this.cities.find(
      (includedCity) => includedCity.name === control.value?.name,
    );
    return validCity ? null : { invalidCity: { value: control.value } };
  }

  matchCrmValueValidator(crmValue: string | null): ValidatorFn {
    return (control: AbstractControl): { [key: string]: unknown } | null => {
      if (!crmValue || !control.value) {
        return null;
      }
      return control.value === crmValue
        ? null
        : { mismatch: { expected: crmValue, actual: control.value } };
    };
  }

  checkIfCityIsValid(cityName: string): boolean {
    const validCity = this.cities.find(
      (includedCity) => includedCity.name === cityName,
    );
    return !!validCity;
  }

  fillDetailsAndDisableFieldsInForm(info: InvoiceDtoWithValidity): void {
    if (info.country) {
      this.selectedCountry = { name: info.country };
      this.updateAvailableCities();
    }

    if (info.city) {
      if (this.checkIfCityIsValid(info.city)) {
        this.showCustomCityNameField = false;

        if (this.customerForm.get('customCityName')) {
          this.customerForm.removeControl('customCityName');
        }

        this.customerForm.patchValue({
          city: { name: info.city },
        });
      } else {
        this.showCustomCityNameField = true;
        this.customerForm.patchValue({
          city: { name: 'أخرى' },
          customCityName: info.city,
        });
      }
    }

    if (info.additionalIdentifierNumber) {
      this.customerForm.patchValue({
        additionalIdentifierNumber: info.additionalIdentifierNumber.toString(),
      });
    }

    if (info.monshaatCompanyName) {
      this.customerForm.patchValue({
        monshaatCompanyName: info.monshaatCompanyName,
      });
    }

    if (info.monshaatOrderNumber) {
      this.customerForm.patchValue({
        monshaatOrderNumber: info.monshaatOrderNumber,
      });
    }

    this.customerForm.patchValue({
      country: this.selectedCountry,
      districtName: info.districtName,
      streetName: info.streetName,
      postalCode: info.postalCode,
      buildingNumber: info.buildingNumber,
      additionalNumber: info.additionalNumber,
      companyName: info.companyName,
    });

    if (info.additionalIdentifier) {
      this.customerForm.patchValue({
        additionalIdentifier: info.additionalIdentifier,
      });
    }

    if (info.vatNumber) {
      this.customerForm.patchValue({
        vatNumber: info.vatNumber,
      });
    } else {
      this.customerForm.get('vatNumber')?.disable();
      this.customerForm.get('nonTaxableEntity')?.setValue(true);
      this.additionalIdentifiers = this.assignAdditionalIdentifiers();
    }

    this.handleMonshaatOrderValidation();

    this.customerForm.get('country')?.disable();

    this.invoiceData.set({
      ...info,
      isValid: this.isFormValid(),
    });
  }

  close(): void {
    this.closeDialog.emit();
  }

  isFormValid(): boolean {
    for (const controlName in this.customerForm.controls) {
      if (
        Object.prototype.hasOwnProperty.call(
          this.customerForm.controls,
          controlName,
        )
      ) {
        const control = this.customerForm.get(controlName);
        if (control?.enabled && control.invalid) {
          return false;
        }
      }
    }
    return true;
  }

  onCountrySelect(): void {
    this.phone.setValue('');
  }

  handlePostalCode(country: { name: string }): void {
    const postalCodeControl = this.customerForm.get('postalCode');
    handlePostalCodeValidationBasedOnCountry(postalCodeControl, country);
  }

  handleMonshaatOrderValidation(): void {
    if (this.showMonshaatFields) {
      const monshaatCompanyNameValidators = [
        Validators.required,
        Validators.maxLength(149),
      ];
      monshaatCompanyNameValidators.push(
        this.matchCrmValueValidator(this.crmMonshaatCompanyName),
      );
      this.monshaatCompanyName?.setValidators(monshaatCompanyNameValidators);

      const monshaatOrderNumberValidators = [
        Validators.required,
        Validators.maxLength(50),
      ];
      monshaatOrderNumberValidators.push(
        this.matchCrmValueValidator(this.crmMonshaatOrderNumber),
      );
      this.monshaatOrderNumber?.setValidators(monshaatOrderNumberValidators);

      const monshaatCommercialRegistrationValidators = [
        Validators.required,
        Validators.pattern(RegexEnum.numberOnly),
      ];
      monshaatCommercialRegistrationValidators.push(
        this.matchCrmValueValidator(this.crmMonshaatCommercialRegistration),
      );

      this.customerForm
        .get('additionalIdentifierNumber')
        ?.setValidators(monshaatCommercialRegistrationValidators);
      this.customerForm.get('additionalIdentifierNumber')?.enable();

      this.additionalIdentifiers = [this.monshaatCommercialRegistration];
      this.selectedIdentifier = this.monshaatCommercialRegistration;
      this.customerForm.patchValue({
        additionalIdentifier: this.monshaatCommercialRegistration,
      });
    } else {
      this.monshaatCompanyName?.clearValidators();
      this.monshaatCompanyName?.setValidators([Validators.maxLength(149)]);

      this.monshaatOrderNumber?.clearValidators();
      this.monshaatOrderNumber?.setValidators([Validators.maxLength(50)]);

      this.customerForm
        .get('additionalIdentifierNumber')
        ?.setValidators([
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern(RegexEnum.numberOnly),
        ]);

      this.additionalIdentifiers = this.assignAdditionalIdentifiers();
    }

    this.monshaatCompanyName?.updateValueAndValidity();
    this.monshaatOrderNumber?.updateValueAndValidity();
    this.customerForm
      .get('additionalIdentifierNumber')
      ?.updateValueAndValidity();
  }

  get mappedDocument(): MappedBillingDocument {
    return this.billingInfoDialogInput.document as MappedBillingDocument;
  }

  assignAdditionalIdentifiers(): string[] {
    if (this.showMonshaatFields) {
      return [this.monshaatCommercialRegistration];
    }
    const nonTaxable = this.customerForm.get('nonTaxableEntity')?.value;
    const { NationalID, NoLegalEntity } = additionalIdentifierNoTaxOptions;

    if (this.mappedDocument.invoice_type === InvoiceZatcaType.B2B) {
      if (nonTaxable) {
        return [...additionalIdentifierOptions, NationalID];
      }
      return [...additionalIdentifierOptions];
    }

    // B2C
    if (nonTaxable) {
      return [NoLegalEntity];
    }
    return [...additionalIdentifierOptions];
  }
}
